<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import es from 'date-fns/locale/es';

const props = defineProps({
  logs: { type: Array, default: () => [] },
});
const { t, locale } = useI18n();

const dateLocale = computed(() => {
  if (locale.value?.startsWith('pt')) return ptBR;
  if (locale.value?.startsWith('es')) return es;
  return undefined;
});

const expanded = ref(false);

const sorted = computed(() =>
  [...props.logs].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
);

const actionLabel = action => {
  const map = {
    confirmed: 'PAINEL.AUDIT_LOG.ACTION_CONFIRMED',
    cancelled: 'PAINEL.AUDIT_LOG.ACTION_CANCELLED',
    reset: 'PAINEL.AUDIT_LOG.ACTION_RESET',
    rescheduled: 'PAINEL.AUDIT_LOG.ACTION_RESCHEDULED',
    linked: 'PAINEL.AUDIT_LOG.ACTION_LINKED',
    unlinked: 'PAINEL.AUDIT_LOG.ACTION_UNLINKED',
  };
  return t(map[action] || action);
};

const relativeTime = iso =>
  formatDistanceToNow(new Date(iso), {
    addSuffix: true,
    locale: dateLocale.value,
  });

const logMeta = log => {
  const who = log.user
    ? t('PAINEL.AUDIT_LOG.BY_USER', { name: log.user.name })
    : t('PAINEL.AUDIT_LOG.BY_SYSTEM');
  return `${who} · ${relativeTime(log.created_at)}`;
};

const logNote = note => `“${note}”`;

const logCount = count => `(${count})`;
</script>

<template>
  <div class="rounded border border-n-strong bg-n-surface-1">
    <button
      type="button"
      class="flex w-full items-center justify-between px-3 py-2 text-xs uppercase text-n-slate-11"
      @click="expanded = !expanded"
    >
      <span
        >{{ t('PAINEL.AUDIT_LOG.TITLE') }} {{ logCount(sorted.length) }}</span
      >
      <span aria-hidden="true">{{ expanded ? '−' : '+' }}</span>
    </button>
    <div v-if="expanded" class="border-t border-n-strong px-3 py-2 text-xs">
      <div v-if="sorted.length === 0" class="text-n-slate-11">
        {{ t('PAINEL.AUDIT_LOG.EMPTY') }}
      </div>
      <ul class="space-y-2">
        <li
          v-for="log in sorted"
          :key="log.id"
          class="border-b border-n-weak pb-2 last:border-b-0 last:pb-0"
        >
          <div class="text-n-slate-12">
            <span class="font-medium">{{ actionLabel(log.action) }}</span>
            <span class="ml-1 text-n-slate-11">{{ logMeta(log) }}</span>
          </div>
          <div v-if="log.note" class="mt-1 italic text-n-slate-11">
            {{ logNote(log.note) }}
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
